(() => {
  'use strict';

  const SHAPES = Object.freeze({ wolke: 'Wolke', herz: 'Herz', stern: 'Stern', fussball: 'Fußball' });
  const COLORS = Object.freeze({ Himmelblau: '#A3D8E1', Pink: '#E8AFCF', Weiss: '#FFFFFF', Schwarz: '#000000' });
  const DEFAULTS = Object.freeze({
    wolke: { body: 'Himmelblau', motif: 'Himmelblau' },
    herz: { body: 'Pink', motif: 'Weiss' },
    stern: { body: 'Weiss', motif: 'Weiss' },
    fussball: { body: 'Weiss', motif: 'Schwarz' }
  });
  const FRONTS = ['Motiv', 'Name', 'NFC-Symbol'];
  const BACKS = ['Nur NFC', 'QR-Code'];
  const PUBLIC_FIELDS = ['shape', 'body', 'motif', 'front', 'back', 'mode', 'quantity'];

  function initialState(shape = 'wolke') {
    if (!Object.hasOwn(SHAPES, shape)) shape = 'wolke';
    return { shape, ...DEFAULTS[shape], front: 'Motiv', back: 'Nur NFC', mode: 'online', quantity: 1, name: '', phone: '', rights: false };
  }

  function publicState(state) {
    return Object.fromEntries(PUBLIC_FIELDS.map(key => [key, state[key]]));
  }

  function restoreState(saved, requestedShape, requestedQuantity) {
    const state = initialState(saved?.shape);
    if (saved && typeof saved === 'object') {
      for (const field of ['body', 'motif']) if (Object.hasOwn(COLORS, saved[field])) state[field] = saved[field];
      if (FRONTS.includes(saved.front)) state.front = saved.front;
      if (BACKS.includes(saved.back)) state.back = saved.back;
      if (['online', 'offline'].includes(saved.mode)) state.mode = saved.mode;
      if (Number.isInteger(saved.quantity) && saved.quantity >= 1 && saved.quantity <= 5) state.quantity = saved.quantity;
    }
    if (Object.hasOwn(SHAPES, requestedShape) && requestedShape !== state.shape) Object.assign(state, { shape: requestedShape }, DEFAULTS[requestedShape]);
    if (/^[1-5]$/.test(String(requestedQuantity))) state.quantity = Number(requestedQuantity);
    return state;
  }

  function variantOptions(state) {
    if (!Object.hasOwn(SHAPES, state.shape) || !Object.hasOwn(COLORS, state.body) || !Object.hasOwn(COLORS, state.motif) || !FRONTS.includes(state.front) || !BACKS.includes(state.back)) return null;
    const defaults = DEFAULTS[state.shape];
    const colors = state.body === defaults.body && state.motif === defaults.motif ? 'Standardfarben' : 'Wunschfarben';
    return [SHAPES[state.shape], `${colors} · ${state.front === 'Name' ? 'mit Name' : 'ohne Name'}`, state.back];
  }

  function createCatalog(variants) {
    const index = new Map(variants.map(variant => [JSON.stringify(variant.options), variant]));
    return state => {
      const options = variantOptions(state);
      const variant = options && index.get(JSON.stringify(options));
      if (!variant || !variant.available || !Number.isSafeInteger(variant.price) || variant.price < 0) return null;
      return variant;
    };
  }

  function validate(state, normalizePhone) {
    const errors = {};
    if (!variantOptions(state)) errors.design = 'unavailable';
    if (state.front === 'Name' && !/^[A-Za-zÄÖÜäöüß-]{1,8}$/.test(state.name.trim())) errors.name = 'name_error';
    if (!Number.isInteger(state.quantity) || state.quantity < 1 || state.quantity > 5) errors.quantity = 'quantity_error';
    if (!['online', 'offline'].includes(state.mode)) errors.design = 'unavailable';
    if (state.mode === 'offline') {
      if (!normalizePhone?.(state.phone)) errors.phone = 'phone_error';
      if (!state.rights) errors.rights = 'rights_error';
    }
    return errors;
  }

  function buildProperties(state, normalizedPhone = '') {
    const properties = {
      Form: SHAPES[state.shape],
      'Tag-Farbe': state.body,
      'Motiv-Farbe': state.motif,
      Vorderseite: state.front,
      Rückseite: state.back,
      _EA_Mode: state.mode,
      Kontaktmodus: 'Online-Kontaktseite',
      _EA_ColorContract: '2026-10-v1',
      _EA_PrivacyVersion: '2026-10-parent-v1'
    };
    if (state.front === 'Name') properties.Wunschname = state.name.trim();
    if (state.mode === 'offline') {
      if (!/^\+[1-9]\d{6,14}$/.test(normalizedPhone)) throw new Error('phone_error');
      Object.assign(properties, {
        Kontaktmodus: 'Offline-Telefonnummer',
        'Offline – Telefonnummer': normalizedPhone,
        _EA_OfflineNotice: '2026-10-v1',
        _EA_RightsConfirmed: '2026-09-v2',
        _EA_PrivacyVersion: '2026-10-offline-v1'
      });
    } else {
      properties._EA_ParentSetup = '2026-10-v1';
    }
    return properties;
  }

  function stateFromLine(line) {
    const props = line.properties || {};
    const shape = Object.keys(SHAPES).find(key => SHAPES[key] === props.Form);
    if (!shape) throw new Error('edit_missing');
    const state = initialState(shape);
    for (const [field, property] of [['body', 'Tag-Farbe'], ['motif', 'Motiv-Farbe']]) {
      const value = props[property] === 'Weiß' ? 'Weiss' : props[property];
      if (!Object.hasOwn(COLORS, value)) throw new Error('edit_missing');
      state[field] = value;
    }
    if (!FRONTS.includes(props.Vorderseite) || !BACKS.includes(props.Rückseite) || !['online', 'offline'].includes(props._EA_Mode)) throw new Error('edit_missing');
    Object.assign(state, { front: props.Vorderseite, back: props.Rückseite, mode: props._EA_Mode, quantity: line.quantity, name: props.Wunschname || '', phone: props['Offline – Telefonnummer'] || '', rights: false });
    return state;
  }

  function propertiesSignature(properties) {
    return JSON.stringify(Object.entries(properties || {}).sort(([a], [b]) => a.localeCompare(b)));
  }

  function sameDesign(line, item) {
    return Number(line.variant_id || line.id) === Number(item.variant_id || item.id)
      && propertiesSignature(line.properties) === propertiesSignature(item.properties)
      && (line.selling_plan_allocation?.selling_plan?.id || null) === (item.selling_plan_allocation?.selling_plan?.id || null);
  }

  function sameLine(line, original) {
    return sameDesign(line, original) && line.quantity === original.quantity;
  }

  class CartError extends Error {
    constructor(code, uncertain = false) { super(code); this.uncertain = uncertain; }
  }

  function replacementState(cart, original, item, expectedQuantity) {
    const sources = cart.items.filter(line => sameLine(line, original));
    const targets = cart.items.filter(line => sameDesign(line, item));
    if (sources.length !== 1 || targets.length !== 1 || targets[0].quantity !== expectedQuantity) throw new CartError('edit_partial', true);
    return { source: sources[0], target: targets[0] };
  }

  async function commitCart(request, item, original = null) {
    if (!original) {
      await request('cart/add.js', { items: Array.isArray(item) ? item : [item] });
      return;
    }
    const before = await request('cart.js');
    const current = before.items.find(line => line.key === original.key);
    if (!current || !sameLine(current, original)) throw new CartError('edit_changed');
    if (Number(current.variant_id || current.id) === Number(item.id)) {
      await request('cart/change.js', { id: current.key, quantity: item.quantity, properties: item.properties });
      return;
    }
    const existingTargets = before.items.filter(line => sameDesign(line, item));
    if (existingTargets.length > 1) throw new CartError('edit_changed');
    const previousQuantity = existingTargets[0]?.quantity || 0;
    const expectedQuantity = previousQuantity + item.quantity;
    await request('cart/add.js', { items: [item] });
    let added;
    try {
      added = replacementState(await request('cart.js'), original, item, expectedQuantity);
    } catch {
      throw new CartError('edit_partial', true);
    }
    try {
      await request('cart/change.js', { id: added.source.key, quantity: 0 });
    } catch (error) {
      if (error.uncertain) throw new CartError('cart_uncertain', true);
      try {
        const unchanged = replacementState(await request('cart.js'), original, item, expectedQuantity);
        await request('cart/change.js', { id: unchanged.target.key, quantity: previousQuantity });
      } catch {
        throw new CartError('edit_partial', true);
      }
      throw new CartError('edit_rollback');
    }
  }

  function packageDiscount(quantity, pricing) {
    if (!Number.isInteger(quantity) || quantity < 1 || quantity > 5) return null;
    if (!pricing || !['base', 'two', 'four'].every(key => Number.isSafeInteger(pricing[key]) && pricing[key] > 0)) return null;
    if (quantity >= 4) return Math.max(0, pricing.base * 4 - pricing.four);
    if (quantity >= 2) return Math.max(0, pricing.base * 2 - pricing.two);
    return 0;
  }

  function selectionStates(state, designs, individual) {
    if (!individual) return [{ ...state }];
    if (!Number.isInteger(state.quantity) || state.quantity < 1 || state.quantity > 5) return [];
    return Array.from({ length: state.quantity }, (_, index) => ({ ...(designs[index] || initialState(state.shape)), quantity: 1 }));
  }

  function selectionTotal(selection, findVariant, pricing) {
    if (!selection.length) return null;
    const quantity = selection.reduce((sum, design) => sum + design.quantity, 0);
    if (!Number.isInteger(quantity) || quantity < 1 || quantity > 5) return null;
    const discount = packageDiscount(quantity, pricing);
    if (discount === null) return null;
    let total = 0;
    for (const design of selection) {
      const unitPrice = findVariant(design)?.price;
      if (!Number.isSafeInteger(unitPrice) || unitPrice < 0 || !Number.isInteger(design.quantity) || design.quantity < 1) return null;
      total += unitPrice * design.quantity;
    }
    return Math.max(0, total - discount);
  }

  const core = { SHAPES, COLORS, DEFAULTS, initialState, publicState, restoreState, variantOptions, createCatalog, validate, buildProperties, stateFromLine, sameLine, commitCart, CartError, packageDiscount, selectionStates, selectionTotal };
  if (typeof module !== 'undefined' && module.exports) module.exports = core;
  if (typeof document === 'undefined') return;

  function mount(root) {
    if (root.dataset.ogMounted) return;
    root.dataset.ogMounted = 'true';
    const one = selector => root.querySelector(selector);
    const all = selector => [...root.querySelectorAll(selector)];
    const copy = JSON.parse(one('[data-og-copy]').textContent);
    const variants = JSON.parse(one('[data-og-variants]').textContent);
    const findVariant = createCatalog(variants);
    const form = one('[data-og-form]');
    const controls = one('[data-og-controls]');
    const submit = one('[data-og-submit]');
    const status = one('[data-og-status]');
    const review = one('[data-og-review]');
    const reviewList = one('[data-og-review-list]');
    const preview = one('[data-og-preview]');
    const motionPreference = globalThis.matchMedia('(prefers-reduced-motion: reduce)');
    const panels = all('[data-og-panel]');
    const appearancePanel = one('[data-og-panel="appearance"]');
    const pricing = JSON.parse(one('[data-bundle-pricing]').textContent);
    const bundleChoices = all('[name="bundle-quantity"]').map(input => ({ input, price: input.closest('label').querySelector('[data-bundle-total]') }));
    const fields = Object.fromEntries(['name', 'phone', 'rights', 'quantity'].map(key => [key, form.elements.namedItem(key)]));
    const route = globalThis.Shopify?.routes?.root || '/';
    const money = new Intl.NumberFormat(document.documentElement.lang || 'de-DE', { style: 'currency', currency: root.dataset.currency || 'EUR' });
    const draftKey = `origin-composer:${root.dataset.productId}:v1`;
    const params = new URL(location.href).searchParams;
    let saved = null;
    try { saved = JSON.parse(sessionStorage.getItem(draftKey)); } catch {}
    let state = restoreState(saved, params.get('form'), params.has('edit') ? null : params.get('quantity'));
    let individual = !params.has('edit') && params.get('design_mode') === 'individual' && state.quantity > 1;
    const requestedAnchor = params.get('anchor');
    let activeDesign = individual && /^[1-5]$/.test(requestedAnchor) ? Math.min(Number(requestedAnchor), state.quantity) - 1 : 0;
    let sameDesignState = { ...state };
    const designs = [];
    let original = null;
    let busy = false;
    let validating = false;
    let locked = false;
    let reviewed = false;
    let phonePromise;
    let view = 'front';
    let started = false;
    let previewAnimation;
    let modelPreview;
    let unmounted = false;

    async function loadPreview() {
      try {
        const { createPreview } = await import(preview.dataset.previewSrc);
        if (unmounted) return;
        modelPreview = createPreview(one('[data-og-model]'), preview, nextView => {
          view = nextView;
          for (const button of all('[data-og-view]')) button.setAttribute('aria-pressed', String(button.dataset.ogView === view));
          preview.setAttribute('aria-label', `${text('preview_label')}: ${shapeText()}, ${text(`color_${state.body}`)}`);
        });
        modelPreview.update(state, COLORS[state.body], COLORS[state.motif]);
        modelPreview.setView(view);
        modelPreview.zoom(preview.classList.contains('is-zoomed'));
        preview.classList.add('has-3d');
      } catch {
        modelPreview?.dispose();
        modelPreview = null;
        one('[data-og-model-status]').textContent = text('preview_unavailable');
      }
    }

    const text = key => copy[key] || key;
    const shapeText = () => text(`shape_${state.shape}`);
    const normalizePhone = value => globalThis.ElternAnkerPhone?.normalize(value) || '';

    function selection() {
      if (individual) designs[activeDesign] = { ...state, quantity: 1 };
      return selectionStates(state, designs, individual);
    }

    function selectDesign(index, focus = false) {
      designs[activeDesign] = { ...state, quantity: 1 };
      const quantity = state.quantity;
      activeDesign = index;
      state = { ...(designs[index] || initialState(state.shape)), quantity };
      reviewed = false;
      status.textContent = '';
      for (const field of ['name', 'phone', 'rights', 'quantity']) setError(field);
      syncInputs();
      if (state.mode === 'offline') loadPhone().catch(() => {});
      render();
      if (focus) {
        appearancePanel.open = true;
        appearancePanel.querySelector('summary').focus();
      }
    }

    function renderFamily() {
      one('[data-og-family]').hidden = Boolean(original) || state.quantity < 2;
      one('[data-og-designs]').hidden = !individual;
      const selected = selection();
      for (const button of all('[data-og-design]')) {
        const index = Number(button.dataset.ogDesign);
        button.hidden = index >= state.quantity;
        button.setAttribute('aria-pressed', String(index === activeDesign));
        const design = selected[index];
        if (design) button.querySelector('small').textContent = text(`shape_${design.shape}`);
      }
      one('[data-og-copy-design]').hidden = !individual || activeDesign === 0;
      one('[data-og-active-design]').textContent = text('editing_anchor').replace('[number]', String(activeDesign + 1));
      one('[data-og-active-design]').hidden = !individual || state.quantity < 2;
    }

    function stopPreviewMotion() {
      previewAnimation?.cancel();
      previewAnimation = null;
    }

    function animatePreview() {
      stopPreviewMotion();
      if (motionPreference.matches || typeof preview.animate !== 'function') return;
      previewAnimation = preview.animate([
        { opacity: .4, transform: 'translateY(6px) scale(.985)' },
        { opacity: 1, transform: 'translateY(0) scale(1)' }
      ], { duration: 280, easing: 'cubic-bezier(.2, .7, .2, 1)' });
    }

    motionPreference.addEventListener('change', stopPreviewMotion);

    // Nach dem Hinzufügen fliegt der gestaltete Anker in einem Bogen zum Warenkorb-Symbol
    async function flyToCart() {
      const target = document.querySelector('.origin-header__cart');
      const source = one(`[data-og-preview-shape="${state.shape}"] svg`);
      if (!target || !source || motionPreference.matches || typeof document.body.animate !== 'function') return;
      const to = target.getBoundingClientRect();
      const from = preview.getBoundingClientRect();
      if (!to.width || !from.width) return;
      const ghost = source.cloneNode(true);
      const originals = [...source.querySelectorAll('path, text, g')];
      ghost.querySelectorAll('path, text, g').forEach((node, index) => {
        const style = getComputedStyle(originals[index]);
        node.setAttribute('fill', style.fill);
        node.setAttribute('stroke', style.stroke);
        if (style.display === 'none') node.setAttribute('display', 'none');
      });
      ghost.style.cssText = 'display:block;width:100%;height:100%;overflow:visible;filter:drop-shadow(0 14px 22px rgba(0,0,0,.22))';
      const size = Math.min(from.width, from.height) * 0.6;
      const left = from.left + from.width / 2 - size / 2;
      const top = from.top + from.height / 2 - size / 2;
      const dx = to.left + to.width / 2 - (left + size / 2);
      const dy = to.top + to.height / 2 - (top + size / 2);
      const flight = document.createElement('div');
      const inner = document.createElement('div');
      flight.style.cssText = `position:fixed;left:${left}px;top:${top}px;width:${size}px;height:${size}px;z-index:10000;pointer-events:none`;
      inner.style.cssText = 'width:100%;height:100%';
      inner.append(ghost);
      flight.append(inner);
      document.body.append(flight);
      const duration = 950;
      // Getrennte Kurven für waagerecht und senkrecht ergeben den Flugbogen
      const across = flight.animate([{ transform: 'translateX(0)' }, { transform: `translateX(${dx}px)` }],
        { duration, easing: 'cubic-bezier(.3,.05,.3,1)', fill: 'forwards' });
      inner.animate([
        { transform: 'translateY(0) scale(1) rotate(0deg)', opacity: 1 },
        { transform: 'translateY(-28px) scale(1.08) rotate(-6deg)', opacity: 1, offset: 0.18 },
        { transform: `translateY(${dy}px) scale(.08) rotate(16deg)`, opacity: 0.9 }
      ], { duration, easing: 'cubic-bezier(.55,0,.85,.4)', fill: 'forwards' });
      try { await across.finished; } catch { /* abgebrochen */ }
      flight.remove();
      await target.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.32)' }, { transform: 'scale(1)' }],
        { duration: 420, easing: 'cubic-bezier(.34,1.56,.64,1)' }).finished.catch(() => {});
    }
    root.closest('.shopify-section')?.addEventListener('shopify:section:unload', () => {
      unmounted = true;
      modelPreview?.dispose();
      stopPreviewMotion();
      motionPreference.removeEventListener('change', stopPreviewMotion);
    }, { once: true });

    function track(event, field) {
      const variant = findVariant(state);
      const total = selectionTotal(selection(), findVariant, pricing);
      const detail = { event, shape: state.shape, contact_mode: state.mode, quantity: state.quantity, variant_id: variant?.id || null, value: total === null ? null : total / 100, currency: root.dataset.currency, individual_designs: individual };
      if (field) detail.field = field;
      root.dispatchEvent(new CustomEvent('origin:composer', { bubbles: true, detail }));
    }

    async function request(path, payload) {
      let response;
      try {
        response = await fetch(route + path, { method: payload ? 'POST' : 'GET', headers: { Accept: 'application/json', ...(payload ? { 'Content-Type': 'application/json' } : {}) }, ...(payload ? { body: JSON.stringify(payload) } : {}), cache: 'no-store' });
      } catch { throw new CartError('cart_uncertain', Boolean(payload)); }
      let result;
      try { result = await response.json(); } catch { throw new CartError('cart_uncertain', Boolean(payload)); }
      if (!response.ok) {
        const error = new CartError('cart_error', response.status >= 500 && Boolean(payload));
        error.providerMessage = typeof result.description === 'string' ? result.description : '';
        throw error;
      }
      return result;
    }

    function loadPhone() {
      if (globalThis.ElternAnkerPhone) return Promise.resolve();
      if (!phonePromise) {
        one('[data-og-phone-status]').textContent = text('loading_phone');
        phonePromise = new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = root.dataset.phoneSrc;
          script.onload = () => globalThis.ElternAnkerPhone ? resolve() : reject(new Error('phone_unavailable'));
          script.onerror = () => reject(new Error('phone_unavailable'));
          document.head.append(script);
        }).then(() => { one('[data-og-phone-status]').textContent = ''; }).catch(error => {
          one('[data-og-phone-status]').textContent = text('phone_unavailable');
          phonePromise = null;
          throw error;
        });
      }
      return phonePromise;
    }

    function syncInputs() {
      for (const field of ['shape', 'body', 'motif', 'front', 'back', 'mode']) {
        for (const input of all(`input[name="${field}"]`)) input.checked = input.value === state[field];
      }
      for (const input of all('input[name="design-mode"]')) input.checked = input.value === (individual ? 'individual' : 'same');
      fields.name.value = state.name;
      fields.phone.value = state.phone;
      fields.rights.checked = state.rights;
      fields.quantity.value = state.quantity;
    }

    function setError(field, key) {
      const target = one(`[data-og-error="${field}"]`);
      if (target) { target.textContent = key ? text(key) : ''; target.hidden = !key; }
      fields[field]?.setAttribute('aria-invalid', String(Boolean(key)));
    }

    function renderPreview() {
      root.style.setProperty('--og-body', COLORS[state.body]);
      root.style.setProperty('--og-motif', COLORS[state.motif]);
      one('[data-og-shape-label]').textContent = shapeText();
      const sideLabel = view === 'free' ? '' : `, ${text(view === 'front' ? 'front_view' : 'back_view')}`;
      preview.setAttribute('aria-label', `${text('preview_label')}: ${shapeText()}, ${text(`color_${state.body}`)}${sideLabel}`);
      for (const item of all('[data-og-preview-shape]')) {
        item.hidden = item.dataset.ogPreviewShape !== state.shape;
        item.querySelector('[data-og-vector-motif]').style.display = view !== 'back' && state.front === 'Motiv' ? '' : 'none';
        item.querySelector('[data-og-vector-nfc]').style.display = view !== 'back' && state.front === 'NFC-Symbol' ? '' : 'none';
        const name = item.querySelector('[data-og-vector-name]');
        name.textContent = state.name || text('name_empty');
        name.style.display = view !== 'back' && state.front === 'Name' ? '' : 'none';
        const back = item.querySelector('[data-og-vector-back]');
        back.style.display = view === 'back' && state.back === 'QR-Code' ? '' : 'none';
        back.style.fill = state.body === 'Schwarz' ? COLORS.Weiss : '#20231f';
      }
      modelPreview?.update(state, COLORS[state.body], COLORS[state.motif]);
    }

    function render() {
      const offline = state.mode === 'offline';
      const personalized = state.front === 'Name';
      one('[data-og-name-field]').hidden = !personalized;
      fields.name.disabled = !personalized;
      one('[data-og-offline-fields]').hidden = !offline;
      one('[data-og-online-help]').hidden = offline;
      fields.phone.disabled = !offline;
      fields.rights.disabled = !offline;
      one('[data-og-summary="appearance"]').textContent = `${shapeText()} · ${text(`color_${state.body}`)} · ${text(`front_${state.front}`)}`;
      one('[data-og-summary="contact"]').textContent = `${text(state.mode)} · ${text(state.back === 'QR-Code' ? 'qr' : 'nfc')}`;
      one('[data-og-summary="quantity"]').textContent = `${state.quantity || '—'} ${text('units')}`;
      for (const field of ['body', 'motif']) one(`[data-og-color-label="${field}"]`).textContent = text(`color_${state[field]}`);
      const variant = findVariant(state);
      for (const choice of bundleChoices) {
        choice.input.checked = Number(choice.input.value) === state.quantity;
        const choiceTotal = selectionTotal([{ ...state, quantity: Number(choice.input.value) }], findVariant, pricing);
        choice.price.textContent = choiceTotal === null ? '—' : money.format(choiceTotal / 100);
      }
      const total = selectionTotal(selection(), findVariant, pricing);
      one('[data-og-total]').textContent = total === null ? '—' : money.format(total / 100);
      let unitLabel = text('unavailable');
      if (total !== null) unitLabel = text('set_total');
      one('[data-og-unit]').textContent = unitLabel;
      for (const [field, selector] of [['front', 'data-og-front-fee'], ['back', 'data-og-back-fee']]) {
        const baseValue = field === 'front' ? 'Motiv' : 'Nur NFC';
        const base = findVariant({ ...state, [field]: baseValue });
        for (const node of all(`[${selector}]`)) {
          const option = findVariant({ ...state, [field]: node.getAttribute(selector) });
          let label = text('unavailable');
          if (option && base) {
            const difference = option.price - base.price;
            label = difference === 0 ? text('included') : `${difference > 0 ? '+ ' : ''}${money.format(difference / 100)}`;
          }
          node.textContent = label;
        }
      }
      submit.disabled = busy || locked;
      let actionKey = original ? 'save' : 'add';
      if (busy) actionKey = 'saving';
      submit.textContent = text(actionKey);
      review.hidden = !reviewed;
      if (reviewed) renderReview();
      renderFamily();
      renderPreview();
    }

    function renderReview() {
      if (individual) {
        const fragment = document.createDocumentFragment();
        const selected = selection();
        selected.forEach((design, index) => {
          const term = document.createElement('dt');
          const definition = document.createElement('dd');
          term.textContent = `${text('anchor')} ${index + 1}`;
          const details = [text(`shape_${design.shape}`), `${text(`color_${design.body}`)} / ${text(`color_${design.motif}`)}`, text(`front_${design.front}`), text(design.mode), text(design.back === 'QR-Code' ? 'qr' : 'nfc')];
          if (design.front === 'Name') details.push(design.name);
          if (design.mode === 'offline') details.push(normalizePhone(design.phone));
          definition.textContent = details.join(' · ');
          fragment.append(term, definition);
        });
        reviewList.replaceChildren(fragment);
        one('[data-og-contents]').textContent = text('set_contents');
        return;
      }
      const rows = [
        ['appearance', `${shapeText()} · ${text(`color_${state.body}`)} / ${text(`color_${state.motif}`)} · ${text(`front_${state.front}`)}`],
        ['contact', `${text(state.mode)} · ${text(state.back === 'QR-Code' ? 'qr' : 'nfc')}`],
        ['quantity', `${state.quantity} ${text('units')}`]
      ];
      if (state.front === 'Name') rows.splice(1, 0, ['name', state.name]);
      if (state.mode === 'offline') rows.push(['phone', normalizePhone(state.phone)]);
      const fragment = document.createDocumentFragment();
      for (const [key, value] of rows) {
        const term = document.createElement('dt');
        const definition = document.createElement('dd');
        term.textContent = text(key);
        definition.textContent = value;
        fragment.append(term, definition);
      }
      reviewList.replaceChildren(fragment);
      one('[data-og-contents]').textContent = text(state.mode === 'offline' ? 'offline_contents' : 'online_contents');
    }

    function showErrors(errors) {
      for (const field of ['name', 'phone', 'rights', 'quantity']) setError(field, errors[field]);
      const first = Object.keys(errors)[0];
      if (!first) return false;
      let panelName = 'appearance';
      if (['phone', 'rights'].includes(first)) panelName = 'contact';
      if (first === 'quantity') panelName = 'quantity';
      one(`[data-og-panel="${panelName}"]`).open = true;
      (fields[first] || one(`[data-og-panel="${panelName}"] summary`)).focus();
      status.textContent = text(errors[first]);
      track('validation_error', first);
      return true;
    }

    function savePublicDraft() {
      try { sessionStorage.setItem(draftKey, JSON.stringify(publicState(state))); } catch {}
    }

    function changed(field) {
      reviewed = false;
      status.textContent = '';
      setError(field);
      if (PUBLIC_FIELDS.includes(field)) savePublicDraft();
      render();
      if (['shape', 'body', 'motif', 'front', 'back'].includes(field)) animatePreview();
      if (!started) { started = true; track('design_start'); }
      if (!['name', 'phone', 'rights'].includes(field)) track('design_option', field);
    }

    form.addEventListener('change', event => {
      const input = event.target;
      if (input.name === 'design-mode') {
        const quantity = state.quantity;
        if (input.value === 'individual') {
          sameDesignState = { ...state };
          state = { ...(designs[0] || state), quantity };
          individual = true;
        } else {
          designs[activeDesign] = { ...state, quantity: 1 };
          state = { ...sameDesignState, quantity };
          individual = false;
        }
        activeDesign = 0;
        syncInputs();
        changed('design_mode');
        return;
      }
      const field = input.name === 'bundle-quantity' ? 'quantity' : input.name;
      if (!PUBLIC_FIELDS.includes(field) && !['name', 'phone', 'rights'].includes(field)) return;
      if (field === 'shape') {
        const previousDefaults = DEFAULTS[state.shape];
        if (state.body === previousDefaults.body && state.motif === previousDefaults.motif) Object.assign(state, DEFAULTS[input.value]);
      }
      if (field === 'quantity') {
        if (individual) designs[activeDesign] = { ...state, quantity: 1 };
        state.quantity = Number(input.value);
        if (individual && activeDesign >= state.quantity) selectDesign(0);
      }
      else if (field === 'rights') state.rights = input.checked;
      else state[field] = input.value;
      if (field === 'mode' && state.mode === 'offline') loadPhone().catch(() => {});
      if (field === 'back') view = 'back';
      else if (['front', 'shape'].includes(field)) view = 'front';
      if (['back', 'front', 'shape'].includes(field)) modelPreview?.setView(view, true);
      for (const button of all('[data-og-view]')) button.setAttribute('aria-pressed', String(button.dataset.ogView === view));
      syncInputs();
      changed(field);
    });

    form.addEventListener('input', event => {
      if (!['name', 'phone'].includes(event.target.name)) return;
      state[event.target.name] = event.target.value;
      changed(event.target.name);
    });

    one('[data-og-reset-colors]').addEventListener('click', () => { Object.assign(state, DEFAULTS[state.shape]); syncInputs(); changed('body'); });
    for (const button of all('[data-og-quantity]')) button.addEventListener('click', () => {
      if (individual) designs[activeDesign] = { ...state, quantity: 1 };
      state.quantity = Math.min(5, Math.max(1, (Number(state.quantity) || 1) + Number(button.dataset.ogQuantity)));
      if (individual && activeDesign >= state.quantity) selectDesign(0);
      syncInputs(); changed('quantity');
    });
    for (const button of all('[data-og-design]')) button.addEventListener('click', () => selectDesign(Number(button.dataset.ogDesign), true));
    one('[data-og-copy-design]').addEventListener('click', () => {
      state = { ...(designs[activeDesign - 1] || initialState(state.shape)), quantity: state.quantity, rights: false };
      syncInputs();
      changed('copy_design');
    });
    for (const button of all('[data-og-view]')) button.addEventListener('click', () => {
      view = button.dataset.ogView;
      modelPreview?.setView(view, true);
      for (const other of all('[data-og-view]')) other.setAttribute('aria-pressed', String(other === button));
      renderPreview();
      animatePreview();
    });
    one('[data-og-zoom]').addEventListener('click', event => {
      const zoomed = preview.classList.toggle('is-zoomed');
      event.currentTarget.setAttribute('aria-pressed', String(zoomed));
      modelPreview?.zoom(zoomed);
    });
    one('[data-og-edit-design]').addEventListener('click', () => { reviewed = false; appearancePanel.open = true; render(); appearancePanel.querySelector('summary').focus(); });

    form.addEventListener('submit', async event => {
      event.preventDefault();
      if (busy || locked || validating) return;
      validating = true;
      if (selection().some(design => design.mode === 'offline')) {
        try { await loadPhone(); } catch { status.textContent = text('phone_unavailable'); validating = false; return; }
      }
      validating = false;
      const selected = selection();
      let errors = validate(state, normalizePhone);
      if (individual && selected.length) {
        errors = {};
        for (const [index, design] of selected.entries()) {
          errors = validate(design, normalizePhone);
          if (!findVariant(design)) errors.design = 'unavailable';
          if (!Object.keys(errors).length) continue;
          selectDesign(index);
          break;
        }
      }
      if (!findVariant(state) || root.dataset.salesEnabled === 'false') errors.design = 'unavailable';
      if (showErrors(errors)) return;
      busy = true;
      controls.disabled = true;
      render();
      status.textContent = text('saving');
      try {
        const items = selected.map(design => ({ id: findVariant(design).id, quantity: design.quantity, properties: buildProperties(design, normalizePhone(design.phone)) }));
        await commitCart(request, original ? items[0] : items, original);
        track(original ? 'edit_cart' : 'add_to_cart');
        if (original || !globalThis.OriginCart) { location.assign(route + 'cart'); return; }
        await flyToCart();
        await globalThis.OriginCart.open();
        busy = false;
        reviewed = false;
        controls.disabled = false;
        status.textContent = '';
        render();
      } catch (error) {
        locked = Boolean(error.uncertain);
        status.textContent = error.providerMessage || text(Object.hasOwn(copy, error.message) ? error.message : 'cart_error');
        one('[data-og-cart-recovery]').hidden = false;
        busy = false;
        controls.disabled = locked;
        render();
      }
    });

    async function initialize() {
      const editKey = params.get('edit');
      if (editKey) {
        status.textContent = text('loading_edit');
        try {
          const cart = await request('cart.js');
          original = cart.items.find(line => line.key === editKey && Number(line.product_id) === Number(root.dataset.productId));
          if (!original) throw new CartError('edit_missing');
          state = stateFromLine(original);
          status.textContent = '';
        } catch (error) {
          locked = true;
          status.textContent = text(Object.hasOwn(copy, error.message) ? error.message : 'cart_error');
          one('[data-og-cart-recovery]').hidden = false;
        }
      } else if (saved) {
        const notice = one('[data-og-notice]');
        notice.textContent = text('restored');
        notice.hidden = false;
      }
      syncInputs();
      controls.disabled = locked;
      if (state.mode === 'offline') loadPhone().catch(() => {});
      render();
      loadPreview();
    }
    initialize();
  }

  document.querySelectorAll('[data-og-composer]').forEach(mount);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('[data-og-composer]').forEach(mount));
})();

